import React, { useEffect } from "react";

const upsertMeta = (selector, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attrs).forEach(([key, value]) => key !== "content" && tag.setAttribute(key, value));
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", attrs.content || "");
  return tag;
};

// Lightweight per-page document metadata + private-preview noindex enforcement.
export default function Seo({ title, description, canonical, image, imageAlt, noindex, type = "website", jsonLd, publishedAt, updatedAt, author }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', { name: "description", content: description });
    }

    let link = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    } else if (link) {
      link.remove();
    }

    const robots = upsertMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" });

    const social = [
      ['meta[property="og:title"]', { property: "og:title", content: title || "" }],
      ['meta[property="og:description"]', { property: "og:description", content: description || "" }],
      ['meta[property="og:type"]', { property: "og:type", content: type }],
      ['meta[property="og:url"]', { property: "og:url", content: canonical || window.location.href }],
      ['meta[property="og:image"]', { property: "og:image", content: image || "" }],
      ['meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt || "" }],
      ['meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" }],
      ['meta[name="twitter:title"]', { name: "twitter:title", content: title || "" }],
      ['meta[name="twitter:description"]', { name: "twitter:description", content: description || "" }],
      ['meta[name="twitter:image"]', { name: "twitter:image", content: image || "" }],
    ];
    social.forEach(([selector, attrs]) => upsertMeta(selector, attrs));

    if (type === "article") {
      upsertMeta('meta[property="article:published_time"]', { property: "article:published_time", content: publishedAt || "" });
      upsertMeta('meta[property="article:modified_time"]', { property: "article:modified_time", content: updatedAt || "" });
      upsertMeta('meta[property="article:author"]', { property: "article:author", content: author || "" });
    }

    let script = document.head.querySelector('script[data-seo-jsonld="true"]');
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-seo-jsonld", "true");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      robots.setAttribute("content", "index, follow");
      const stale = document.head.querySelector('script[data-seo-jsonld="true"]');
      if (stale) stale.remove();
    };
  }, [title, description, canonical, image, imageAlt, noindex, type, jsonLd, publishedAt, updatedAt, author]);
  return null;
}
